import type { Viewport, Camera2D } from '../hex/camera2d';
import type { HexMapState } from './renderer';
import type { MapModel } from '../map/mapModel';
import { DEFAULT_MODAL_THEME } from '../../modal/ui/ModalTheme';
import { ContentBuilder } from '../../modal/ui/ContentBuilder';
import { flowMeasureAndLayout, flowRender } from '../../modal/ui/flowLayout';
import { getTutorialStep, getTutorialTargetRect } from '../../tutorials/runtime';
import { t } from '../../i18n/translations';

export function drawTutorialOverlay(
    ctx: CanvasRenderingContext2D,
    state: HexMapState,
    model: MapModel,
    camera: Camera2D,
    viewport: Viewport,
): void {
    if (!state.tutorial) return;
    const isCompleted = state.tutorial.completed;
    const step = getTutorialStep(state);
    if (!isCompleted && !step) return;
    const title = isCompleted
        ? t(state.config.ui.language, 'tutorial.done.title')
        : step?.title || '';
    const body = isCompleted ? t(state.config.ui.language, 'tutorial.done.body') : step?.body || '';

    const targetRect =
        !isCompleted && step
            ? getTutorialTargetRect(state, step, camera, viewport, (id) => {
                  return model.tiles.find((tile) => tile.id === id) || null;
              })
            : null;

    ctx.save();

    const panel = layoutTipPanel(ctx, title, body, targetRect, viewport, isCompleted);
    ctx.fillStyle = DEFAULT_MODAL_THEME.colors.frameFill;
    ctx.strokeStyle = DEFAULT_MODAL_THEME.colors.frameStroke;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.rect(panel.rect.x, panel.rect.y, panel.rect.w, panel.rect.h);
    ctx.fill();
    ctx.stroke();

    flowRender(ctx, {
        items: panel.items,
        contentRect: panel.contentRect,
        scrollY: 0,
        theme: DEFAULT_MODAL_THEME,
        hitTargetsOut: [],
    });

    if (targetRect) {
        const start = {
            x: panel.rect.x + panel.rect.w,
            y: panel.rect.y + panel.rect.h / 2,
        };
        const end = {
            x: targetRect.x + targetRect.w / 2,
            y: targetRect.y + targetRect.h / 2,
        };
        const startX = panel.rect.x > end.x ? panel.rect.x : panel.rect.x + panel.rect.w;
        ctx.strokeStyle = 'rgba(255,255,255,0.65)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(startX, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();

        ctx.fillStyle = 'rgba(255,255,255,0.75)';
        ctx.beginPath();
        ctx.arc(end.x, end.y, 4, 0, Math.PI * 2);
        ctx.fill();
    }

    ctx.restore();
}

function layoutTipPanel(
    ctx: CanvasRenderingContext2D,
    title: string,
    body: string,
    targetRect: { x: number; y: number; w: number; h: number } | null,
    viewport: Viewport,
    forceTop: boolean,
) {
    const builder = new ContentBuilder();
    builder.text(title, 'header');
    builder.spacer(4);
    const bodyLines = wrapText(ctx, body, 260, DEFAULT_MODAL_THEME.fonts.body);
    if (bodyLines.length === 0) {
        builder.text(body, 'body');
    } else {
        bodyLines.forEach((line) => {
            if (line.length === 0) {
                builder.spacer(DEFAULT_MODAL_THEME.lineHeights.body);
            } else {
                builder.text(line, 'body');
            }
        });
    }
    const items = builder.getItems();

    const maxW = 260;
    const padding = 10;
    const measureRect = { x: 0, y: 0, w: maxW, h: 10 };
    const layout = flowMeasureAndLayout(ctx, items, measureRect, DEFAULT_MODAL_THEME);
    const panelH = layout.contentHeight + padding * 2;
    const panelW = maxW + padding * 2;

    let x = 16;
    let y = viewport.height - panelH - 16;
    if (forceTop) {
        x = Math.max(16, (viewport.width - panelW) / 2);
        y = 16;
    }
    if (targetRect) {
        const rightSpace = viewport.width - (targetRect.x + targetRect.w) - 20;
        const leftSpace = targetRect.x - 20;
        if (rightSpace >= panelW) {
            x = targetRect.x + targetRect.w + 20;
            y = Math.max(16, Math.min(viewport.height - panelH - 16, targetRect.y - panelH / 2));
        } else if (leftSpace >= panelW) {
            x = targetRect.x - panelW - 20;
            y = Math.max(16, Math.min(viewport.height - panelH - 16, targetRect.y - panelH / 2));
        }
    }

    const panelRect = { x, y, w: panelW, h: panelH };
    const innerRect = {
        x: panelRect.x + padding,
        y: panelRect.y + padding,
        w: maxW,
        h: panelH - padding * 2,
    };

    flowMeasureAndLayout(ctx, items, innerRect, DEFAULT_MODAL_THEME);

    return { rect: panelRect, contentRect: innerRect, items };
}

function wrapText(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    font: string,
): string[] {
    const lines: string[] = [];
    ctx.save();
    ctx.font = font;
    const paragraphs = text.split('\n');
    paragraphs.forEach((paragraph, idx) => {
        if (!paragraph.trim()) {
            lines.push('');
            return;
        }
        const words = paragraph.split(/\s+/);
        let current = words[0] || '';
        for (let i = 1; i < words.length; i += 1) {
            const next = `${current} ${words[i]}`;
            if (ctx.measureText(next).width <= maxWidth) {
                current = next;
            } else {
                lines.push(current);
                current = words[i];
            }
        }
        lines.push(current);
        if (idx < paragraphs.length - 1) lines.push('');
    });
    ctx.restore();
    return lines;
}
